import { safeUrl, mediaUrl, sanitizeReportHtml, plainText } from './reportContent';
import { formatReportPerformance } from './reportNumbers';

export const SECTION_LABELS = {
  graficos: 'Valores destacados', sabias_que: '¿Sabías que?', dicho: 'Dicho del día',
  direccionalidad: 'Direccionalidad', acciones_rentables: 'Acciones rentables', video: 'Vídeo',
  satira: 'Sátira bursátil', podcast: 'Podcast', resumen: 'Resumen', consejo: 'Consejo'
};
export const FAVORITE_TYPES = ['graficos', 'sabias_que', 'dicho', 'direccionalidad', 'acciones_rentables', 'video', 'satira'];
const field = (node, name) => (node[name] && node[name].und) || [];
const htmlField = (node, name) => field(node, name).map(item => item.safe_value || item.value || '').join('\n');
export const reportSlug = title => /^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/.test(String(title)) ? String(title).replace(/\//g, '-') : '';
export function reportDate(value) {
  const text = String(value || '');
  const match = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  const date = match ? new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])) : new Date(typeof value === 'number' || /^\d{10}$/.test(text) ? Number(value) * 1000 : text.slice(0, 10) + 'T12:00:00');
  return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}
export function mapReport(raw = {}) {
  const slug = reportSlug(raw.title);
  const sections = Object.keys(raw.subnodes || {}).map(key => {
    const type = key.replace(/^field_wr_/, '').replace(/_ref$/, '');
    const refs = Array.isArray(raw.subnodes[key]) ? raw.subnodes[key] : [];
    return { key, type, label: SECTION_LABELS[type] || type.replace(/_/g, ' '), refs,
      restricted: refs.some(id => String(id) === '-1'), favoriteSupported: FAVORITE_TYPES.includes(type) };
  }).filter(section => section.refs.length).sort((a, b) => {
    if (a.restricted !== b.restricted) return a.restricted ? 1 : -1;
    const rank = type => FAVORITE_TYPES.includes(type) ? FAVORITE_TYPES.indexOf(type) : 100;
    return rank(a.type) - rank(b.type);
  });
  return { id: String(raw.nid || ''), slug, title: `Informe semanal · ${reportDate(raw.title || raw.created)}`, date: reportDate(raw.title || raw.created),
    year: slug.slice(-4), image: safeUrl(raw.pic_pc), unpublished: Number(raw.status) === 0, sections };
}
export function mapCatalog(raw = {}) {
  return { reports: (Array.isArray(raw.informes) ? raw.informes : []).map(mapReport).filter(item => item.slug),
    page: Number(raw.page) || 0, pages: Math.max(0, Number(raw.total_pages) || 0), total: Math.max(0, Number(raw.total) || 0) };
}
export function mapFavorites(raw = {}) {
  const value = raw.favourites || {};
  return Object.fromEntries(FAVORITE_TYPES.filter(key => Array.isArray(value[key])).map(key => [key, [...new Set(value[key].map(String))]]));
}
export function accessNotice(features = {}) {
  if (features.has_informe_access_full || features.is_admin_user || features.is_developer_user) return '';
  return features.has_informe_access ? 'Tu plan incluye el informe más reciente. El archivo requiere acceso completo.' : 'Puedes leer las secciones abiertas del último informe. El resto del contenido requiere una suscripción.';
}
export function mapNode(raw = {}, type) {
  const html = raw.content || raw.dicho_body || htmlField(raw, 'field_wr_acciones_rentables_desc') || '';
  const charts = [];
  for (let i = 1; i <= 4; i++) {
    const history = raw[`grafico_${i}_data`];
    const ticker = raw[`grafico_${i}_ticker`] || '';
    const description = sanitizeReportHtml(htmlField(raw, `field_wr_grafico_${i}_description`));
    if (!history && !ticker && !description) continue;
    const points = history && history.s === 'ok' && Array.isArray(history.t) ? history.t.map((time, index) => [Number(time) * 1000, Number((history.c || [])[index])]).filter(point => point.every(Number.isFinite) && point[1] > 0).sort((a, b) => a[0] - b[0]) : [];
    charts.push({ key: String(i), ticker, description, kind: 'stock', points, error: history && history.s === 'error' ? 'No se pudo cargar este gráfico.' : '', empty: !points.length });
  }
  const tables = [['sem', 'Semana'], ['men', 'Mes'], ['tri', 'Trimestre']].map(([suffix, label]) => ({ label,
    rows: field(raw, `field_wr_acciones_rentables_${suffix}`).filter(item => {
      const value = plainText(item.safe_value || item.value).trim();
      return value && value !== '0';
    }).map(item => {
      const value = plainText(item.safe_value || item.value);
      const match = value.match(/^\s*(.*?)\s*\(([^)]+)\)\s*$/);
      return { ticker: match ? match[1].trim() : '', performance: formatReportPerformance(match ? match[2] : value) };
    }).filter(row => row.ticker || row.performance) })).filter(table => table.rows.length);
  const media = safeUrl(raw.url_video || raw.url_podcast);
  return { id: String(raw.nid || ''), type, title: String(raw.title || ''), html: sanitizeReportHtml(html), image: safeUrl(raw.imagen),
    author: String(raw.dicho_autor || ''), authorUrl: safeUrl(raw.dicho_autor_url), media, embed: mediaUrl(media),
    charts, tables, directionDate: (String(raw.title || '').match(/(\d{2})-(\d{2})-(\d{4})$/) || []).slice(1).reverse().join('-') };
}
